/**
 * Native fetch wrapper for the field PWA. Bearer-only auth (no cookies)
 * — cookies don't survive the cross-origin pairing flow cleanly, and
 * the device JWT in IndexedDB is the source of truth.
 *
 * Provides the AuthHttp interface @argos/auth-client expects for its
 * pair/unlock helpers, plus a getJson/postJson/etc. surface for app
 * code.
 */
import type { AuthHttp } from '@argos/auth-client';

const BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');

let _bearer: string | null = null;

/** Set the in-memory bearer token. Called after pair/unlock; cleared
 *  on logout / re-pair. The PWA never serializes the unwrapped token
 *  to disk — Dexie holds the wrapped form only. */
export function setBearerToken(token: string | null): void {
  _bearer = token;
}

export function getBearerToken(): string | null {
  return _bearer;
}

interface RequestOpts {
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

export class HttpError extends Error {
  constructor(public status: number, public body: unknown) {
    super(`HTTP ${status}`);
  }
}

async function request<TRes>(
  method: string,
  path: string,
  body?: unknown,
  opts: RequestOpts = {},
): Promise<TRes> {
  const url = path.startsWith('http') ? path : `${BASE}${path}`;
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(opts.headers ?? {}),
  };
  if (_bearer) headers['Authorization'] = `Bearer ${_bearer}`;
  let payload: BodyInit | null = null;
  if (body !== undefined && body !== null) {
    if (body instanceof FormData) {
      payload = body;
      // Don't set Content-Type; fetch sets the multipart boundary.
    } else {
      headers['Content-Type'] = 'application/json';
      payload = JSON.stringify(body);
    }
  }

  const res = await fetch(url, {
    method,
    headers,
    body: payload,
    signal: opts.signal,
  });

  if (res.status === 204) return undefined as TRes;
  let data: unknown;
  const ct = res.headers.get('Content-Type') || '';
  if (ct.includes('application/json')) {
    data = await res.json();
  } else {
    data = await res.text();
  }

  // The ALB in front of the API has a SPA-fallback rule (404 → 200
  // /index.html) so that browser refreshes on client-side routes work.
  // It catches API 404s too, handing back the HTML shell with a 200.
  // To app code that's indistinguishable from a successful JSON call
  // until JSON.parse blows up somewhere downstream — so detect it here
  // and surface a clean synthetic 404 instead.
  if (
    res.ok &&
    typeof data === 'string' &&
    (ct.includes('text/html') || /^\s*<(!doctype|html)\b/i.test(data))
  ) {
    throw new HttpError(404, { error: 'not_found', detail: 'API route not found (ALB returned the SPA shell).' });
  }

  if (!res.ok) {
    throw new HttpError(res.status, data);
  }
  return data as TRes;
}

export const apiClient = {
  getJson<T>(path: string, opts?: RequestOpts): Promise<T> {
    return request<T>('GET', path, undefined, opts);
  },
  postJson<TReq, TRes>(path: string, body: TReq, opts?: RequestOpts): Promise<TRes> {
    return request<TRes>('POST', path, body, opts);
  },
  putJson<TReq, TRes>(path: string, body: TReq, opts?: RequestOpts): Promise<TRes> {
    return request<TRes>('PUT', path, body, opts);
  },
  delete<TRes>(path: string, opts?: RequestOpts): Promise<TRes> {
    return request<TRes>('DELETE', path, undefined, opts);
  },
  postForm<TRes>(path: string, form: FormData, opts?: RequestOpts): Promise<TRes> {
    return request<TRes>('POST', path, form, opts);
  },
};

/** Slimmer surface that matches the AuthHttp interface from
 *  @argos/auth-client. Pair/unlock helpers consume this. */
export const authHttp: AuthHttp = {
  postJson: apiClient.postJson,
  getJson: apiClient.getJson,
};
